import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { useMailProvider } from './MailProviderContext';
import type { EmailAttachment, DraftAttachmentMeta, MailAttachmentEntry } from '../../lib/stores/mail/types';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';
import { MailOfferAttach } from './MailOfferAttach';
import { MailRecipientInput, Recipient } from './MailRecipientInput';
import { buildSignatureHtml } from '../../lib/utils/emailSignatureHtml';
import {
  X, Send, Loader, ChevronDown, FileText, Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, Link as LinkIcon, Minus, ChevronUp, Mail, MailOpen,
  Paperclip, Trash2, AlignLeft, AlignCenter, AlignRight, RemoveFormatting,
  Palette, PenLine, Plus,
} from 'lucide-react';

interface MailComposeDrawerProps {
  organizationId: string;
  userId: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result?.toString().split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const SIG_TOOLTIP_KEY = 'gmail_sig_tooltip_seen';
const PRESET_COLORS = ['#37352F', '#2383E2', '#E91E8C', '#0F7B6C', '#D9730D', '#DC2626', '#7C3AED'];

// Avvolge la firma in un contenitore identificabile per la sostituzione affidabile
function signatureWrapper(row: any | null): string {
  const inner = row ? buildSignatureHtml(row) : '';
  return `<div data-signature="1"><div>--</div>${inner}</div>`;
}

function parseRecipientsStr(s: string): Recipient[] {
  return (s || '').split(',').map(x => x.trim()).filter(Boolean).map(part => {
    const m = part.match(/^(.*?)<(.+?)>$/);
    if (m) return { email: m[2].trim(), name: (m[1].trim().replace(/^"|"$/g, '') || null) };
    return { email: part };
  });
}

// Citazione del messaggio originale (HTML reso fedelmente nel contentEditable)
function buildQuote(msg: any, mode: 'reply' | 'replyAll' | 'forward'): string {
  const date = new Date(msg.internal_date).toLocaleString('it-IT');
  const inner = DOMPurify.sanitize(msg.body_html || (msg.body_text ? `<pre style="white-space:pre-wrap;font-family:inherit">${msg.body_text}</pre>` : ''));
  const fmt = (arr?: Array<{ email: string; name?: string }>) => (arr || []).map(a => a.name ? `${a.name} <${a.email}>` : a.email).join(', ');
  const header = mode === 'forward'
    ? `<p style="color:#888;font-size:12px;margin:0 0 6px;"><strong>---------- Messaggio inoltrato ----------</strong><br>Da: ${msg.from_name || ''} &lt;${msg.from_email}&gt;<br>Data: ${date}<br>Oggetto: ${msg.subject || ''}<br>A: ${fmt(msg.to_emails)}</p>`
    : `<p style="color:#888;font-size:12px;margin:0 0 6px;">Il ${date}, ${msg.from_name || msg.from_email} ha scritto:</p>`;
  return `<br><div class="gmail_quote" style="border-left:2px solid #d3d3d0;padding-left:12px;margin-top:8px;color:#555;">${header}${inner}</div>`;
}

export function MailComposeDrawer({ organizationId, userId }: MailComposeDrawerProps) {
  const { useStore, displayName, provider } = useMailProvider();
  const {
    replyingToMessageId, replyingToThreadId, replyMode, selectedMessages, templates,
    composeMinimized, savingDraft, editingDraft, composePrefill, setComposeMinimized,
    sendEmail, saveDraft, deleteDraft, resetCompose, loadDrafts,
  } = useStore();

  const { toast } = useApp();

  const [toR, setToR] = useState<Recipient[]>([]);
  const [ccR, setCcR] = useState<Recipient[]>([]);
  const [bccR, setBccR] = useState<Recipient[]>([]);
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  // Traccia apertura: default dalla preferenza utente (user_profiles.mail_tracking_default),
  // sovrascrivibile per-mail col toggle nel footer.
  const [trackOpen, setTrackOpen] = useState(true);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('user_profiles')
      .select('mail_tracking_default')
      .eq('id', userId)
      .single()
      .then(({ data, error }) => {
        if (!cancelled && !error && data && typeof data.mail_tracking_default === 'boolean') {
          setTrackOpen(data.mail_tracking_default);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const toStr = toR.map(r => r.email).join(', ');
  const ccStr = ccR.map(r => r.email).join(', ');
  const bccStr = bccR.map(r => r.email).join(', ');

  const [files, setFiles] = useState<File[]>([]);
  const [prebuilt, setPrebuilt] = useState<EmailAttachment[]>([]);
  // Solo Outlook: allegati GIÀ presenti sulla bozza riaperta (metadati da -drafts-list).
  // Mostrati come chip, rimovibili; nel set autoritativo viaggiano come keep-ref
  // (nome + size Graph, senza byte). Gmail non li produce mai (DraftSummary senza attachments).
  const [remote, setRemote] = useState<DraftAttachmentMeta[]>([]);
  // Outlook — semantica a 3 stati sugli allegati della bozza: finché la sessione NON
  // tocca gli allegati (add/remove), il save NON invia il campo `attachments` (undefined)
  // → l'edge NON tocca gli allegati già sulla bozza (nessun data-loss alla riapertura).
  // Diventa true al primo add/remove; da lì il save invia la lista AUTORITATIVA (anche []
  // → cancellazione esplicita). Al reopen di una bozza il drawer si rimonta → parte false.
  const [attachmentsTouched, setAttachmentsTouched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [hasSignature, setHasSignature] = useState<boolean | null>(null);
  const [signatures, setSignatures] = useState<any[]>([]);
  const [showSignatures, setShowSignatures] = useState(false);
  const [showSigTooltip, setShowSigTooltip] = useState(false);
  const [sigLoaded, setSigLoaded] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyInitRef = useRef(false);

  const getBodyHtml = () => bodyRef.current?.innerHTML || '';

  // Comando di formattazione (mantiene il focus nell'editor)
  const exec = (cmd: string, value?: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, value);
    triggerAutoSave();
  };

  const linkedClientId = selectedMessages.find(m => m.client_id)?.client_id || null;

  // ─── Firme ───
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase
          .from('email_signatures').select('*').eq('user_id', userId)
          .order('is_default', { ascending: false }).order('created_at', { ascending: false });
        const sigs = data || [];
        setSignatures(sigs);
        setHasSignature(sigs.length > 0);
      } catch { setHasSignature(false); }
      finally { setSigLoaded(true); }
    };
    load();
  }, [userId]);

  // Tooltip firma prima apertura
  useEffect(() => {
    if (!localStorage.getItem(SIG_TOOLTIP_KEY)) {
      setShowSigTooltip(true);
      const t = setTimeout(() => { setShowSigTooltip(false); localStorage.setItem(SIG_TOOLTIP_KEY, '1'); }, 6000);
      return () => clearTimeout(t);
    }
  }, []);

  // Prefill destinatario da openCompose (trigger esterni al drawer, es. card kanban)
  useEffect(() => {
    if (!composePrefill || editingDraft || replyingToMessageId) return;
    setToR([{ email: composePrefill.to, name: composePrefill.name ?? null }]);
  }, [composePrefill, editingDraft, replyingToMessageId]);

  // Prefill destinatari/oggetto per reply/replyAll/forward
  useEffect(() => {
    if (!replyingToMessageId || selectedMessages.length === 0 || editingDraft) return;
    const replyMsg = selectedMessages.find(m => m.message_id === replyingToMessageId);
    if (!replyMsg) return;
    const origSubj = replyMsg.subject || '';
    if (replyMode === 'forward') {
      setToR([]); setCcR([]); setBccR([]);
      setSubject(origSubj.match(/^(fwd|fw):/i) ? origSubj : `Fwd: ${origSubj}`);
    } else if (replyMode === 'replyAll') {
      setToR([{ email: replyMsg.from_email, name: replyMsg.from_name }]);
      const others = [...(replyMsg.to_emails as any || []), ...(replyMsg.cc_emails as any || [])].filter(a => a.email && a.email !== replyMsg.from_email);
      const seen = new Set<string>();
      setCcR(others.filter(a => { const k = a.email.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; }).map(a => ({ email: a.email, name: a.name || null })));
      setShowCcBcc(seen.size > 0);
      setSubject(origSubj.match(/^re:/i) ? origSubj : `Re: ${origSubj}`);
    } else {
      setToR([{ email: replyMsg.from_email, name: replyMsg.from_name }]);
      setSubject(origSubj.match(/^re:/i) ? origSubj : `Re: ${origSubj}`);
    }
  }, [replyingToMessageId, replyMode, selectedMessages, editingDraft]);

  // Inizializza il corpo UNA volta
  useEffect(() => {
    if (!bodyRef.current || !sigLoaded || bodyInitRef.current) return;
    bodyInitRef.current = true;

    if (editingDraft) {
      setToR(parseRecipientsStr(editingDraft.to));
      if (editingDraft.cc) { setCcR(parseRecipientsStr(editingDraft.cc)); setShowCcBcc(true); }
      setSubject(editingDraft.subject || '');
      // Allegati già sulla bozza (solo Outlook): idratati come chip. Restano "non
      // toccati" finché l'utente non li modifica → il save/send li preserva (3 stati).
      if (editingDraft.attachments?.length) setRemote(editingDraft.attachments);
      bodyRef.current.innerHTML = DOMPurify.sanitize(editingDraft.body_html || '<div><br></div>');
      placeCaretAtStart();
      return;
    }

    const def = signatures.find(s => s.is_default) || signatures[0] || null;
    let quote = '';
    if (replyingToMessageId) {
      const replyMsg = selectedMessages.find(m => m.message_id === replyingToMessageId);
      if (replyMsg) quote = buildQuote(replyMsg, replyMode === 'forward' ? 'forward' : (replyMode as any));
    }
    bodyRef.current.innerHTML = `<div><br></div>${signatureWrapper(def)}${quote}`;
    placeCaretAtStart();
  }, [sigLoaded, signatures, replyingToMessageId, replyMode, selectedMessages, editingDraft]);

  // Incolla sanificato (evita HTML "sporco"/pericoloso da altri programmi)
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      e.preventDefault();
      document.execCommand('insertHTML', false, DOMPurify.sanitize(html));
      triggerAutoSave();
    } else if (text) {
      e.preventDefault();
      document.execCommand('insertText', false, text);
      triggerAutoSave();
    }
  };

  const placeCaretAtStart = () => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    // Enter crea <div> (coerente, stile Gmail) invece di comportamenti misti
    try { document.execCommand('defaultParagraphSeparator', false, 'div'); } catch {}
    const range = document.createRange();
    const sel = window.getSelection();
    const first = el.firstChild || el;
    range.setStart(first, 0);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  // Auto-save
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerAutoSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const replyMsg = replyingToMessageId && selectedMessages.find(m => m.message_id === replyingToMessageId);
      const inReplyTo = replyMsg ? `<${replyMsg.message_id}@mail.gmail.com>` : undefined;
      if (provider === 'microsoft') {
        // Outlook: la bozza deve conservare gli allegati (sync idempotente nome+size lato
        // edge) e il threading nativo (createReply* via inReplyTo/replyMode). replyMode
        // solo su reply/forward reale (replyMsg valorizzato), come per l'invio.
        // Allegati (3 stati): se la sessione NON li ha toccati → `undefined` (l'edge NON
        // tocca gli allegati esistenti); se toccati → set autoritativo completo (keep-ref
        // dei remoti mantenuti + nuovi con contenuto; anche [] = cancella tutto).
        const attachments = attachmentsTouched ? await buildDraftSet() : undefined;
        await saveDraft({ organizationId, userId, to: toStr || undefined, cc: ccStr || undefined, bcc: bccStr || undefined, subject: subject.trim() || undefined, bodyHtml: getBodyHtml(), threadId: replyingToThreadId || undefined, inReplyTo, references: inReplyTo, replyMode: replyMsg ? replyMode : undefined, attachments });
        return;
      }
      // Gmail: chiamata invariata (byte-identica al flusso pre-esistente, nessun allegato in bozza).
      await saveDraft({ organizationId, userId, to: toStr || undefined, cc: ccStr || undefined, bcc: bccStr || undefined, subject: subject.trim() || undefined, bodyHtml: getBodyHtml(), threadId: replyingToThreadId || undefined, inReplyTo, references: inReplyTo });
    }, 2000);
  };
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);
  useEffect(() => { triggerAutoSave(); }, [toStr, ccStr, bccStr, subject]);
  // Outlook: auto-save anche al cambio allegati (add/remove, inclusi i remoti) → la sync
  // lato edge riflette il set corrente. Il debounce coalizza col trigger dei campi testuali
  // (nessuna doppia bozza). Gmail invariato: nessun trigger extra (non invia allegati in bozza).
  useEffect(() => { if (provider === 'microsoft') triggerAutoSave(); }, [files, prebuilt, remote]);

  const hasContent = () => !!(toStr || subject.trim() || getBodyHtml().replace(/<[^>]*>/g, '').trim());

  const buildAttachments = async (): Promise<EmailAttachment[]> => {
    const fromFiles = await Promise.all(files.map(async f => ({ filename: f.name, content: await fileToBase64(f), contentType: f.type || 'application/octet-stream' })));
    return [...prebuilt, ...fromFiles];
  };

  // Set autoritativo per bozze/invio Outlook: keep-ref dei remoti mantenuti (nome +
  // size Graph, senza ritrasmettere i byte) + nuovi allegati con contenuto base64.
  const buildDraftSet = async (): Promise<MailAttachmentEntry[]> => {
    const keepRefs = remote.map(r => ({ filename: r.filename, graph_size: r.size, contentType: r.contentType }));
    return [...keepRefs, ...await buildAttachments()];
  };

  const handleSend = async () => {
    const bodyHtml = getBodyHtml();
    if (!toR.length || !subject.trim() || !bodyHtml.replace(/<[^>]*>/g, '').trim()) {
      toast.error('Compila destinatario, oggetto e messaggio');
      return;
    }
    setSending(true);
    // Annulla l'auto-save pendente (come handleClose): un debounce armato che scattasse
    // DURANTE l'invio ripristinerebbe currentDraftId dopo che sendEmail lo ha azzerato,
    // e la deleteDraft a valle cancellerebbe l'email appena inviata (con ImmutableId la
    // bozza inviata mantiene lo STESSO id). sendEmail attende comunque i save già in volo.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    try {
      const replyMsg = replyingToMessageId && selectedMessages.find(m => m.message_id === replyingToMessageId);
      const inReplyTo = replyMsg ? `<${replyMsg.message_id}@mail.gmail.com>` : undefined;
      // Outlook (3 stati, come per le bozze): non toccati → undefined (la bozza viene
      // inviata con gli allegati che già ha, inclusi quelli di una bozza riaperta);
      // toccati → set autoritativo keep-ref + nuovi. Il draftId lo inietta sendEmail
      // dallo store (la bozza stessa viene inviata, mai un messaggio-copia senza allegati).
      // Gmail: flusso invariato (contenuti da files/prebuilt, nessun keep-ref).
      const attachments = provider === 'microsoft'
        ? (attachmentsTouched ? await buildDraftSet() : undefined)
        : await buildAttachments().then(a => (a.length ? a : undefined));
      const success = await sendEmail({
        organizationId, userId, to: toStr, cc: ccStr || undefined, bcc: bccStr || undefined,
        subject: subject.trim(), bodyHtml, threadId: replyingToThreadId || undefined, inReplyTo, references: inReplyTo,
        // Solo su reply/forward reale (replyMsg valorizzato): su compose nuovo lo
        // stato replyMode resta all'ultimo valore usato (resetCompose non lo azzera),
        // quindi va omesso per non far degradare erroneamente l'edge Outlook.
        replyMode: replyMsg ? replyMode : undefined,
        attachments,
        track: trackOpen,
      });
      if (success) {
        await deleteDraft(organizationId, userId);
        loadDrafts(organizationId, userId);
        toast.success('Email inviata');
        resetCompose();
      } else toast.error('Errore nell\'invio dell\'email');
    } catch (e) {
      console.error('Send error:', e);
      toast.error('Errore nell\'invio dell\'email');
    } finally { setSending(false); }
  };

  const handleClose = async () => {
    if (hasContent()) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (provider === 'microsoft') {
        // Outlook: preserva allegati + threading nativo anche nel salvataggio in chiusura.
        const replyMsg = replyingToMessageId && selectedMessages.find(m => m.message_id === replyingToMessageId);
        const inReplyTo = replyMsg ? `<${replyMsg.message_id}@mail.gmail.com>` : undefined;
        // Allegati (3 stati): non toccati → `undefined` (l'edge lascia intatti quelli
        // esistenti); toccati → set autoritativo keep-ref + nuovi, anche [] (cancella tutto).
        const attachments = attachmentsTouched ? await buildDraftSet() : undefined;
        await saveDraft({ organizationId, userId, to: toStr || undefined, cc: ccStr || undefined, bcc: bccStr || undefined, subject: subject.trim() || undefined, bodyHtml: getBodyHtml(), threadId: replyingToThreadId || undefined, inReplyTo, references: inReplyTo, replyMode: replyMsg ? replyMode : undefined, attachments });
      } else {
        // Gmail: chiamata invariata (byte-identica al flusso pre-esistente).
        await saveDraft({ organizationId, userId, to: toStr || undefined, cc: ccStr || undefined, bcc: bccStr || undefined, subject: subject.trim() || undefined, bodyHtml: getBodyHtml(), threadId: replyingToThreadId || undefined });
      }
      loadDrafts(organizationId, userId);
      toast.success(`Bozza salvata su ${displayName}`);
    }
    resetCompose();
  };

  const handleDiscard = async () => {
    // Annulla l'auto-save pendente (come handleSend/handleClose): un debounce che
    // scattasse dopo la delete ricreerebbe la bozza appena eliminata.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    await deleteDraft(organizationId, userId);
    loadDrafts(organizationId, userId);
    resetCompose();
  };

  const handleSelectTemplate = (templateBodyHtml: string) => {
    if (!bodyRef.current) return;
    const wrap = bodyRef.current.querySelector('[data-signature]');
    const sigAndQuote = wrap ? bodyRef.current.innerHTML.substring(bodyRef.current.innerHTML.indexOf((wrap as HTMLElement).outerHTML)) : '';
    bodyRef.current.innerHTML = `<div>${DOMPurify.sanitize(templateBodyHtml)}</div>${sigAndQuote}`;
    setShowTemplates(false);
    triggerAutoSave();
  };

  const handleAddLink = () => {
    const url = window.prompt('Inserisci URL:');
    if (!url) return;
    bodyRef.current?.focus();
    const sel = window.getSelection();
    if (sel && sel.toString()) document.execCommand('createLink', false, url);
    else document.execCommand('insertHTML', false, `<a href="${url}">${url}</a>`);
    triggerAutoSave();
  };

  const openSignatureEditor = () => {
    setShowSigTooltip(false);
    localStorage.setItem(SIG_TOOLTIP_KEY, '1');
    window.open('/signature-generator', '_blank', 'noopener');
  };

  // Sostituisce la firma: trova il contenitore [data-signature] e ne rimpiazza il contenuto
  const applySignature = (sig: any) => {
    setShowSignatures(false);
    const el = bodyRef.current;
    if (!el) return;
    const wrap = el.querySelector('[data-signature]');
    if (wrap) {
      wrap.innerHTML = `<div>--</div>${buildSignatureHtml(sig)}`;
    } else {
      const quote = el.querySelector('.gmail_quote');
      const wrapperHtml = signatureWrapper(sig);
      if (quote) quote.insertAdjacentHTML('beforebegin', wrapperHtml);
      else el.insertAdjacentHTML('beforeend', wrapperHtml);
    }
    triggerAutoSave();
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) { setFiles(prev => [...prev, ...picked]); setAttachmentsTouched(true); }
    e.target.value = '';
  };

  const headerTitle = replyingToMessageId ? 'Rispondi' : 'Nuova email';
  const attachmentCount = files.length + prebuilt.length + remote.length;

  useDrawerEscape(!composeMinimized,()=>{void handleClose();},67);

  // ─── PILLOLA MINIMIZZATA ───
  if (composeMinimized) {
    const previewSubject = subject.trim() || (replyingToMessageId ? 'Rispondi' : 'Nuova email');
    return createPortal((
      <div className="fixed bottom-4 right-4 z-[66] flex items-center gap-2 pl-3 pr-1 py-1.5 max-w-[360px] rounded-full bg-[#37352F] dark:bg-gray-800 text-white shadow-2xl border border-white/10">
        <button onClick={() => setComposeMinimized(false)} className="flex items-center gap-2 min-w-0 hover:opacity-90 transition-opacity">
          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-xs font-medium truncate max-w-[220px]">{previewSubject}</span>
          {savingDraft && <Loader className="h-3 w-3 animate-spin flex-shrink-0 opacity-60" />}
        </button>
        <button onClick={() => setComposeMinimized(false)} className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0" title="Espandi"><ChevronUp className="h-3.5 w-3.5" /></button>
        <button onClick={handleClose} className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0" title="Chiudi e salva bozza"><X className="h-3.5 w-3.5" /></button>
      </div>
    ), document.body);
  }

  const tbBtn = 'p-1.5 rounded transition-colors text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50';

  // ─── DRAWER ESPANSO ───
  return createPortal((
    <>
      <div className="fixed inset-0 z-[66] bg-black/10 md:top-12 max-md:!left-0 animate-[fadeIn_0.25s_ease-out]" style={{ left: 'var(--sidebar-width, 0px)' }} onClick={() => setComposeMinimized(true)} />
      <div className="fixed top-0 right-0 bottom-0 z-[67] bg-white dark:bg-gray-900 shadow-2xl overflow-hidden md:top-12 animate-[slideInRight_0.25s_ease-out] flex flex-col w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Mail className="h-4 w-4 text-[#37352F] dark:text-white flex-shrink-0" />
            <h3 className="text-sm font-semibold text-[#37352F] dark:text-white truncate">{headerTitle}</h3>
            {savingDraft && <span className="flex items-center gap-1 text-[10px] text-[#787774] dark:text-white/40 flex-shrink-0"><Loader className="h-3 w-3 animate-spin" /> Salvataggio…</span>}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button onClick={() => setComposeMinimized(true)} className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60" title="Riduci a icona"><Minus className="h-4 w-4" /></button>
            <button onClick={handleClose} className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60" title="Chiudi e salva bozza"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          <MailRecipientInput label="A:" recipients={toR} onChange={setToR} autoFocus={!replyingToMessageId}
            trailing={!showCcBcc ? <button onClick={() => setShowCcBcc(true)} className="px-2 py-1 text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white flex-shrink-0 self-start mt-1">Cc/Bcc</button> : undefined} />
          {showCcBcc && (<>
            <MailRecipientInput label="Cc:" recipients={ccR} onChange={setCcR} />
            <MailRecipientInput label="Bcc:" recipients={bccR} onChange={setBccR} />
          </>)}
          {/* Subject */}
          <div className="flex items-center border-b border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] px-4">
            <label className="py-2 text-xs font-medium text-[#787774] dark:text-white/40 w-12">Ogg:</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="flex-1 py-2 text-sm bg-transparent border-none focus:outline-none text-[#37352F] dark:text-white" placeholder="Oggetto" />
          </div>

          {/* Body contentEditable (renderizza HTML reale: firma, citazioni) */}
          <div
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            onInput={triggerAutoSave}
            onPaste={onPaste}
            className="gmail-ce flex-1 px-5 py-4 text-sm text-[#37352F] dark:text-white/90 focus:outline-none overflow-y-auto"
            style={{ minHeight: 240 }}
          />

          {/* Allegati */}
          {attachmentCount > 0 && (
            <div className="px-4 py-2 border-t border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] flex flex-wrap gap-2">
              {remote.map((a, i) => (
                <span key={`r-${a.id}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[rgba(55,53,47,0.05)] dark:bg-white/10 text-xs text-[#37352F] dark:text-white">
                  <Paperclip className="h-3.5 w-3.5 text-[#787774] flex-shrink-0" /> <span className="max-w-[180px] truncate">{a.filename}</span> <span className="text-[#9B9A97] flex-shrink-0">({humanSize(a.size)})</span>
                  <button onClick={() => { setRemote(prev => prev.filter((_, j) => j !== i)); setAttachmentsTouched(true); }} className="text-[#787774] hover:text-red-500 flex-shrink-0"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {prebuilt.map((a, i) => (
                <span key={`pb-${i}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[rgba(55,53,47,0.05)] dark:bg-white/10 text-xs text-[#37352F] dark:text-white">
                  <FileText className="h-3.5 w-3.5 text-[#DC2626] flex-shrink-0" /> <span className="max-w-[180px] truncate">{a.filename}</span>
                  <button onClick={() => { setPrebuilt(prev => prev.filter((_, j) => j !== i)); setAttachmentsTouched(true); }} className="text-[#787774] hover:text-red-500 flex-shrink-0"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {files.map((f, i) => (
                <span key={`f-${i}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[rgba(55,53,47,0.05)] dark:bg-white/10 text-xs text-[#37352F] dark:text-white">
                  <Paperclip className="h-3.5 w-3.5 text-[#787774] flex-shrink-0" /> <span className="max-w-[180px] truncate">{f.name}</span> <span className="text-[#9B9A97] flex-shrink-0">({humanSize(f.size)})</span>
                  <button onClick={() => { setFiles(prev => prev.filter((_, j) => j !== i)); setAttachmentsTouched(true); }} className="text-[#787774] hover:text-red-500 flex-shrink-0"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}

          <style>{`
            .gmail-ce a { color: #2383E2; text-decoration: underline; }
            .gmail-ce img { max-width: 100%; height: auto; }
            .gmail-ce table { max-width: 100%; }
            .gmail-ce .gmail_quote { border-left: 2px solid #d3d3d0; padding-left: 12px; margin-top: 8px; color: #6b6b6b; }
            .dark .gmail-ce .gmail_quote { border-left-color: #4a4a48; color: rgba(255,255,255,0.55); }
            .gmail-ce:empty:before { content: 'Scrivi il tuo messaggio...'; color: #9B9A97; }
          `}</style>
        </div>

        {/* Toolbar formattazione */}
        <div className="flex items-center gap-0.5 px-3 py-1.5 border-t border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] flex-shrink-0 overflow-x-auto" onMouseDown={(e) => e.preventDefault()}>
          <button type="button" onClick={() => exec('bold')} className={tbBtn} title="Grassetto"><Bold className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('italic')} className={tbBtn} title="Corsivo"><Italic className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('underline')} className={tbBtn} title="Sottolineato"><Underline className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('strikeThrough')} className={tbBtn} title="Barrato"><Strikethrough className="h-3.5 w-3.5" /></button>
          <div className="relative">
            <button type="button" onClick={() => setShowColors(v => !v)} className={tbBtn} title="Colore testo"><Palette className="h-3.5 w-3.5" /></button>
            {showColors && (
              <div className="absolute bottom-full left-0 mb-1 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 flex gap-1 z-20">
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => { exec('foreColor', c); setShowColors(false); }} className="w-5 h-5 rounded-full border border-black/10" style={{ backgroundColor: c }} title={c} />
                ))}
              </div>
            )}
          </div>
          <span className="w-px h-4 bg-[rgba(55,53,47,0.12)] dark:bg-white/10 mx-1" />
          <button type="button" onClick={() => exec('justifyLeft')} className={tbBtn} title="Allinea a sinistra"><AlignLeft className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('justifyCenter')} className={tbBtn} title="Centra"><AlignCenter className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('justifyRight')} className={tbBtn} title="Allinea a destra"><AlignRight className="h-3.5 w-3.5" /></button>
          <span className="w-px h-4 bg-[rgba(55,53,47,0.12)] dark:bg-white/10 mx-1" />
          <button type="button" onClick={() => exec('insertUnorderedList')} className={tbBtn} title="Elenco puntato"><List className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('insertOrderedList')} className={tbBtn} title="Elenco numerato"><ListOrdered className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={handleAddLink} className={tbBtn} title="Inserisci link"><LinkIcon className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => exec('removeFormat')} className={tbBtn} title="Rimuovi formattazione"><RemoveFormatting className="h-3.5 w-3.5" /></button>
        </div>

        {/* Barra azioni */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 flex-shrink-0">
          <div className="flex items-center gap-1">
            <button onClick={handleSend} disabled={sending || toR.length === 0} className="flex items-center gap-1.5 px-4 py-2 bg-[#2383E2] text-white text-sm font-medium rounded-lg hover:bg-[#1a6fc9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {sending ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Invia
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickFiles} />
            <button onClick={() => fileInputRef.current?.click()} className="p-2 rounded-md text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5" title="Allega file"><Paperclip className="h-4 w-4" /></button>
            <button
              onClick={() => setTrackOpen((v) => !v)}
              className={`p-2 rounded-md transition-colors ${trackOpen ? 'text-[#2383E2] bg-[rgba(35,131,226,0.1)]' : 'text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'}`}
              title={trackOpen ? 'Tracciamento apertura attivo' : 'Tracciamento apertura disattivato'}
            >
              <MailOpen className="h-4 w-4" />
            </button>
            <button onClick={handleAddLink} className="p-2 rounded-md text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5" title="Inserisci link"><LinkIcon className="h-4 w-4" /></button>
            <div className="relative">
              <button onClick={() => setShowOffer(v => !v)} className="flex items-center gap-1 px-2 py-2 rounded-md text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5" title="Allega un'offerta">
                <FileText className="h-4 w-4" /> <span className="text-xs hidden sm:inline">Offerta</span>
              </button>
              <MailOfferAttach open={showOffer} onClose={() => setShowOffer(false)} clientId={linkedClientId}
                onAttachPdf={(att) => { setPrebuilt(prev => [...prev, att]); setAttachmentsTouched(true); }}
                onInsertLink={(html) => { bodyRef.current?.focus(); document.execCommand('insertHTML', false, html); triggerAutoSave(); }} />
            </div>
            <div className="relative">
              <button onClick={() => setShowTemplates(!showTemplates)} className="flex items-center gap-1 px-2 py-2 text-xs text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md">
                <FileText className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Template</span> <ChevronDown className="h-3 w-3" />
              </button>
              {showTemplates && templates.length > 0 && (
                <div className="absolute bottom-full left-0 mb-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-10">
                  {templates.map(t => (
                    <button key={t.id} onClick={() => handleSelectTemplate(t.body_html)} className="w-full text-left px-3 py-2 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5">
                      <div className="font-medium truncate">{t.name}</div>
                      {t.category !== 'general' && <div className="text-[10px] text-[#787774] dark:text-white/40">{t.category}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Selettore firma */}
            <div className="relative">
              <button onClick={() => setShowSignatures(v => !v)} className={`flex items-center gap-1 px-2 py-2 text-xs rounded-md transition-colors ${hasSignature === false ? 'text-[#2383E2] bg-[#2383E2]/10 hover:bg-[#2383E2]/15' : 'text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'}`} title="Scegli la firma">
                {hasSignature === false ? <Plus className="h-3.5 w-3.5" /> : <PenLine className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">{hasSignature === false ? 'Crea firma' : 'Firma'}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {showSigTooltip && (
                <div className="absolute bottom-full left-0 mb-2 w-56 px-3 py-2 bg-[#37352F] dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl z-30">
                  Da qui scegli, crei e modifichi la tua firma email.
                  <div className="absolute -bottom-1 left-6 w-2 h-2 bg-[#37352F] dark:bg-gray-700 rotate-45" />
                </div>
              )}
              {showSignatures && (
                <div className="absolute bottom-full left-0 mb-1 w-60 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-20">
                  {signatures.map(s => (
                    <button key={s.id} onClick={() => applySignature(s)} className="w-full text-left px-3 py-2 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{s.name || 'Firma'}</span>
                      {s.is_default && <span className="text-[9px] text-[#2383E2] bg-[#2383E2]/10 px-1 rounded flex-shrink-0">predefinita</span>}
                    </button>
                  ))}
                  {signatures.length === 0 && <div className="px-3 py-2 text-xs text-[#787774]">Nessuna firma salvata</div>}
                  <div className="border-t border-[rgba(55,53,47,0.08)] dark:border-gray-700 my-1" />
                  <button onClick={openSignatureEditor} className="w-full text-left px-3 py-2 text-xs text-[#2383E2] hover:bg-[#2383E2]/5 flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Gestisci firme…</button>
                </div>
              )}
            </div>
          </div>

          <button onClick={handleDiscard} className="p-2 rounded-md text-[#787774] dark:text-white/60 hover:text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0" title="Elimina bozza"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </>
  ), document.body);
}
